import { Controller } from "@hotwired/stimulus";

const LS_CORAL_KEY = "coralJWT";
const ADMIN = "ADMIN";
const MODERATOR = "MODERATOR";
const STAFF = "STAFF";
const MEMBER = "MEMBER";
const COMMENTER = "COMMENTER";
const STAFF_ROLES = [ADMIN, MODERATOR, STAFF, MEMBER];

class JWTExpiredError extends Error {}
class JWTMissingError extends Error {}
class JWTMalformedError extends Error {}

export default class extends Controller {
  static values = {
    storyId: String,
    storyUrl: String,
    coralBaseUrl: String,
    className: String,
    loginHref: String,
    className: String,
    type: String,
  };
  static targets = [
    "script",
    "uploadLink",
    "livestream",
    "liveStreamButtonText",
  ];

  connect() {
    // Only load comments when they're about to be viewed
    if (this.hasIntersectionObserverSupport()) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.initializeComments();
          observer.disconnect();
        }
      }, {
        rootMargin: "100px" // Load when within 100px of viewport
      });
      observer.observe(this.element);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.initializeComments();
    }
  }

  initializeComments() {
    const loggedIn = document.querySelector('html').dataset.loggedIn

    if (loggedIn !== 'true') this.clearJWT()

    if (loggedIn === 'true') {
      this.getCoralUser()
        .then(([jwt, decodedJWT]) => {
          this.loadCoral(jwt, decodedJWT)

          if (this.isStaff(decodedJWT)) this.showUploadLink()
          else if (this.typeValue === 'livestream') this.showLiveStream()
        })
        .catch((err) => {
          this.loadCoral()
        })
    } else {
      this.loadCoral()
      if (this.typeValue === 'livestream') this.showLiveStream()
    }
  }

  hasIntersectionObserverSupport() {
    return 'IntersectionObserver' in window;
  }

  async getCoralUser() {
    const coralJWT = localStorage.getItem(LS_CORAL_KEY)

    if (coralJWT) {
      try {
        const decodedJWT = this.decodeJWT(coralJWT)

        // Add buffer time to refresh token before it actually expires
        const now = new Date()
        const exp = new Date(decodedJWT.exp * 1000)
        const bufferTime = 5 * 60 * 1000 // 5 minutes in milliseconds

        if (exp > new Date(now.getTime() + bufferTime)) {
          return [coralJWT, decodedJWT]
        } else {
          throw new JWTExpiredError()
        }
      } catch (error) {
        this.clearJWT()
      }
    }

    // Prevent multiple simultaneous requests for JWT
    if (this.jwtFetchPromise) {
      return this.jwtFetchPromise
    }

    this.jwtFetchPromise = fetch('/coral/login')
      .then((res) => res.text())
      .then((jwt) => {
        localStorage.setItem(LS_CORAL_KEY, jwt)
        this.jwtFetchPromise = null
        return [jwt, this.decodeJWT(jwt)]
      })
      .catch((err) => {
        this.jwtFetchPromise = null
        throw new JWTMissingError()
      })

    return this.jwtFetchPromise
  }

  clearJWT() {
    localStorage.removeItem(LS_CORAL_KEY);
  }

  decodeJWT(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      // TODO log to sentry
      this.clearJWT();
      throw new JWTMalformedError();
    }
  }

  loadCoral(jwt, decodedJWT) {
    // Check if Coral is already loaded globally
    if (window.Coral) {
      this.initializeCoralEmbed(jwt, decodedJWT)
      return
    }

    // Only load the script once across the site
    if (!document.querySelector('script[src*="/assets/js/embed.js"]')) {
      this.scriptTarget.src = this.coralBaseUrlValue + '/assets/js/embed.js'
      this.scriptTarget.async = true // Changed to true for performance
      this.scriptTarget.defer = true
      this.scriptTarget.onload = () => {
        this.initializeCoralEmbed(jwt, decodedJWT)
      }
    } else {
      // Wait for the existing script to load
      const checkInterval = setInterval(() => {
        if (window.Coral) {
          clearInterval(checkInterval)
          this.initializeCoralEmbed(jwt, decodedJWT)
        }
      }, 100)
    }
  }

  initializeCoralEmbed(jwt, decodedJWT) {
    let staffStatus = this.determineStaffStatus(decodedJWT);
    const containerClassName = `${this.classNameValue}-${this.typeValue} ${staffStatus}`;

    const config = {
      containerClassName,
      id: 'coral_thread',
      autoRender: false,
      rootURL: this.coralBaseUrlValue,
      accessToken: jwt,
      storyID: this.storyIdValue,
      storyURL: this.storyUrlValue,
      events: (events) => {
        events.on('loginPrompt', () => {
          location.href = this.loginHrefValue
        })
      },
    }

    const streamEmbed = Coral.createStreamEmbed(config)
    streamEmbed.render()
  }

  determineStaffStatus(decodedJWT) {
    try {
      if (!decodedJWT) return 'logged-out'

      switch (decodedJWT.user.role) {
        case ADMIN:
        case MEMBER:
        case MODERATOR:
        case STAFF:
          return 'staff'
        case COMMENTER:
          return 'not-staff'
        default:
          return 'logged-out'
      }
    } catch (e) {
      return 'logged-out'
    }
  }

  // TODO this uses query selectors as we don't have access to adding target properties on elements added by the coral embed script
  toggleCoralInputForm() {
    const coralInputFormElement = document
      .querySelector("#coral_thread div")
      .shadowRoot.querySelector(
        "#post-comment-form .coral.coral-createComment"
      );

    if (
      coralInputFormElement &&
      window
        .getComputedStyle(coralInputFormElement)
        .getPropertyValue("display") == "none"
    ) {
      coralInputFormElement.style.display = "block";

      this.liveStreamButtonTextTarget.innerHTML = "Stäng";
    } else if (coralInputFormElement) {
      coralInputFormElement.style.display = "none";

      this.liveStreamButtonTextTarget.innerHTML = "Skriv inlägg";
    }
  }

  // show methods only go one-way since they are evaluated on
  // every page-load and are largely static on user or page type
  showUploadLink() {
    this.uploadLinkTarget.classList.replace("hidden", "flex");
  }
  showLiveStream() {
    this.livestreamTarget.classList.replace("hidden", "flex");
  }

  isStaff(decodedJWT) {
    try {
      const role = decodedJWT.user.role;
      return STAFF_ROLES.includes(role);
    } catch (error) {
      // catch and return false in case we can't access the role property
      return false;
    }
  }
}
